import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
} from "../../lib";
import { clearDb } from "../db";
import { clearLogoutPending } from "../../lib/auth/logout_pending";
import { withLoginClaim } from "../../lib/auth/pending_logout_retry";
import { startDeadlineRequest, startSignedInSession } from "../../lib/auth/session_deadlines";
import { toProofSafeSchemaError } from "../../model/auth_proof";

import {
  doAUTH_SET,
  doSETTINGS_SET,
  doREQUEST_ERROR,
  doREQUEST_COMPLETE,
  doPROJECT_SETTINGS_LIST_SET,
  doFAVORITE_LIST_SET,
  doIDENTITY_SET,
  doPROFILE_SET,
} from "../state";

export const REQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT =
  "REQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT";
export const doREQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT = createDoRequestAction(
  REQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT,
);

export const requestIdentityMfaVerificationCodeSubmitActionCreators = {
  doREQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT,
};

export function* identityMfaVerificationCodeSubmit({
  payload,
  setRequestResult,
}: any): SagaIterator {
  debug("saga*identityMfaVerificationCodeSubmit");

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        email: v
          .string()
          .email({ tlds: { allow: false } })
          .exist(),
        verificationCode: v.string().exist(),
      }),
    });
  } catch (error) {
    // the error reduced to `isJoi` and a message: the ValidationError itself quotes the rejected
    // payload, verification code included (see `toProofSafeSchemaError`)
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT,
          requestId: payload?.requestId,
          error: toProofSafeSchemaError(error),
        },
        setRequestResult,
      ),
    );
  }

  const { email, verificationCode } = payload;

  // holds the login claim and waits out a logout retry in flight, so neither a retry already out
  // nor one started while this request is on the wire can clear its cookie; see `withLoginClaim`
  return yield* withLoginClaim(function* () {
    const deadlineRequest = startDeadlineRequest();
    const result = yield request({
      method: "post",
      url: `${API_URL}/v1/web/identity-mfa-verification-code-submit`,
      data: {
        email,
        verificationCode,
      },
    });

    if (!result.success || result.error) {
      // never the raw error, which carries the request body (verification code included) on `config.data`,
      // and no `fromPayload`; see "Credential-Carrying Sagas" in src/store/saga/AGENTS.md
      debug("saga*identityMfaVerificationCodeSubmit failed", {
        statusCode: result.statusCode,
        type: result.type,
      });
      return yield put(
        doREQUEST_ERROR(
          {
            fromType: REQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT,
            requestId: payload.requestId,
            url: "/v1/web/identity-mfa-verification-code-submit",
            statusCode: result.statusCode,
            type: result.type,
            payload: result.payload,
            headers: result.headers,
          },
          setRequestResult,
        ),
      );
    }

    const {
      profile,
      settings,
      projectSettingsList,
      workspaceFavoriteList,
      accessToken,
      accessTokenExpiresInSeconds,
      refreshTokenExpiresInSeconds,
      identity,
    } = result.payload;

    // Ensure any stale local state is cleared before establishing a new session
    yield call(clearDb);

    yield put(
      doAUTH_SET({
        accessToken,
        accessTokenExpiresInSeconds,
        refreshTokenExpiresInSeconds,
        ...startSignedInSession(result.payload, deadlineRequest),
      }),
    );

    // the marker retires on a successful login, the same as on the password path
    yield call(clearLogoutPending);

    yield put(doPROFILE_SET(profile || {}));
    yield put(doSETTINGS_SET(settings));
    yield put(doPROJECT_SETTINGS_LIST_SET(projectSettingsList));
    yield put(doFAVORITE_LIST_SET(workspaceFavoriteList));
    yield put(doIDENTITY_SET(identity));

    yield put(
      doREQUEST_COMPLETE(
        {
          fromType: REQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT,
          requestId: payload.requestId,
          ...result,
        },
        setRequestResult,
      ),
    );
  });
}

export function* identityMfaVerificationCodeSubmitSaga() {
  yield takeEvery(REQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT, identityMfaVerificationCodeSubmit);
}
